// src/pages/pregnancy/PregnancyPages.jsx
// Routes:
//   /demo/pregnancy/myjourney  → PregnancyMyJourney
//   /demo/pregnancy/health     → PregnancyHealthRecord
//   /demo/pregnancy/community  → PregnancyCommunity
//   /demo/pregnancy/connect    → PregnancyConnect
//   /demo/pregnancy/events     → PregnancyEvents
//   /demo/pregnancy/products   → PregnancyProducts
//
// Add to App.js:
//   import { PregnancyMyJourney, PregnancyHealthRecord, PregnancyCommunity,
//            PregnancyConnect, PregnancyEvents, PregnancyProducts } from './pages/pregnancy/PregnancyPages';
//
//   <Route path="/demo/pregnancy/myjourney" element={<PregnancyMyJourney />} />
//   <Route path="/demo/pregnancy/health"    element={<PregnancyHealthRecord />} />
//   <Route path="/demo/pregnancy/community" element={<PregnancyCommunity />} />
//   <Route path="/demo/pregnancy/connect"   element={<PregnancyConnect />} />
//   <Route path="/demo/pregnancy/events"    element={<PregnancyEvents />} />
//   <Route path="/demo/pregnancy/products"  element={<PregnancyProducts />} />

import { useState } from "react";
import NovaAvatarImg from "../../components/NovaAvatarImg";

// Pregnancy: --coral / --coral-l / --coral-d
const C = {
  primary: "var(--coral)", primaryL: "var(--coral-l)", primaryM: "#8BC9BC", primaryDk: "var(--coral-d)",
  sand: "var(--sand)", sand2: "var(--sand-2)", sand3: "var(--sand-3)", white: "var(--card)", border: "var(--border)",
  muted: "var(--muted)", text: "var(--text)", text2: "var(--text-2)",
  pink: "var(--pink)", pinkL: "var(--pink-l)", pinkM: "var(--pink-m)", pinkDk: "var(--pink-dk)",
  ppL: "var(--purple-l)", ppM: "var(--purple-m)", ppD: "var(--purple-d)",
  amL: "var(--amber-l)", am: "var(--amber)", blueL: "var(--blue-l)", blueD: "var(--blue-d)",
  sageL: "var(--sage-l)", sageM: "var(--sage-m)", sageD: "var(--sage-d)", r: "var(--r)",
};

// ── Shared micro components ───────────────────────────────────────────
const Card = ({ children, style = {} }) => (
  <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: C.r, padding: 16, marginBottom: 10, ...style }}>{children}</div>
);
const SLabel = ({ children, style = {} }) => (
  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: C.muted, marginBottom: 10, ...style }}>{children}</div>
);
const Btn = ({ children, variant = "primary", style = {}, onClick }) => {
  const vs = {
    primary: { background: C.primary,  color: "#fff",      border: "none" },
    ghost:   { background: C.primaryL, color: C.primaryDk, border: `1px solid ${C.primaryM}` },
    sand:    { background: C.sand,     color: C.muted,     border: `1px solid ${C.border}` },
    pink:    { background: C.pink,     color: "#fff",      border: "none" },
  };
  return <button onClick={onClick} style={{ fontFamily:"inherit", fontSize:12, fontWeight:700, borderRadius:999, padding:"7px 16px", cursor:"pointer", ...vs[variant], ...style }}>{children}</button>;
};
const Tag = ({ children, bg = C.primaryL, color = C.primaryDk, border = C.primaryM }) => (
  <span style={{ fontSize:10, fontWeight:600, padding:"2px 9px", borderRadius:999, background:bg, color, border:`1px solid ${border}`, display:"inline-block" }}>{children}</span>
);
const NovaBanner = ({ text, prompts = [] }) => (
  <div style={{ background:C.primaryL, border:`1px solid ${C.primaryM}`, borderRadius:C.r, padding:"12px 14px", display:"flex", gap:10, alignItems:"flex-start", marginBottom:14 }}>
    <NovaAvatarImg size={28} alt="" />
    <div style={{ flex:1 }}>
      <div style={{ fontSize:12, color:C.primaryDk, lineHeight:1.55, marginBottom:prompts.length ? 8 : 0 }}>{text}</div>
      {prompts.length > 0 && (
        <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
          {prompts.map(p => <button key={p} style={{ fontFamily:"inherit", fontSize:11, padding:"4px 10px", background:C.white, border:`1px solid ${C.primaryM}`, borderRadius:999, cursor:"pointer", color:C.primaryDk }}>{p}</button>)}
        </div>
      )}
    </div>
    <button style={{ fontFamily:"inherit", fontSize:11, fontWeight:600, color:C.primary, background:"none", border:"none", cursor:"pointer", whiteSpace:"nowrap" }}>Open Nova →</button>
  </div>
);

// Week-by-week content (sample key weeks)
const WEEKLY_CONTENT = {
  20: { baby:"Your baby is the size of a banana — about 10 inches, 10 oz. Halfway there!", you:"You may feel baby's kicks more regularly now. The anatomy scan happens around this week.", milestone:"Anatomy scan", focus:"Glucose screening soon, think about birth plan" },
  21: { baby:"Baby can hear your voice and may respond to loud sounds. Taste buds are forming.", you:"You might notice Braxton Hicks contractions — irregular and painless. Normal at this stage.", milestone:"Halfway point passed", focus:"Start thinking about childbirth classes" },
  28: { baby:"Baby can open their eyes and blink. Brain is developing rapidly — talk, read, sing.", you:"Third trimester begins. Kicks should be regular — start kick counting if you haven't.", milestone:"Third trimester starts", focus:"Start GBS test planning, hospital bag" },
};

// ══════════════════════════════════════════════════════════════════════
// PREGNANCY MY JOURNEY
// ══════════════════════════════════════════════════════════════════════
export function PregnancyMyJourney({ navigate }) {
  const nav = navigate || (() => {});
  const [tab, setTab] = useState("thisweek");
  const [mood, setMood] = useState(null);
  const [symptoms, setSymptoms] = useState([]);
  const week = WEEKLY_CONTENT[20];

  const MOODS = ["😩","😢","😞","😐","😑","🙂","😊","😄","🤩"];
  const MOOD_LABELS = ["Rough","Sad","Low","Okay","Meh","Good","Great","Happy","Amazing"];
  const SYMPTOMS = ["Nausea","Heartburn","Backache","Round lig. pain","Swelling","Fatigue","Headache","Insomnia","Constipation","Braxton Hicks","Shortness of breath","Breast tenderness"];
  const toggleSym = s => setSymptoms(p => p.includes(s) ? p.filter(x=>x!==s) : [...p,s]);

  const MILESTONES = [
    { n:1,  label:"BFP & dating scan",    date:"Oct 15, 2025", done:true },
    { n:2,  label:"First trimester screen",date:"Nov 8, 2025",  done:true },
    { n:3,  label:"NIPT result",          date:"Nov 20, 2025", done:true },
    { n:4,  label:"Anatomy scan — Week 20",date:"Mar 23, 2026", done:false, current:true },
    { n:5,  label:"Glucose screening",    date:"~Week 24",     done:false },
    { n:6,  label:"GBS test",             date:"~Week 36",     done:false },
    { n:7,  label:"Birth!",               date:"~Aug 10, 2026",done:false },
  ];

  return (
    <div style={{ fontFamily:"'Plus Jakarta Sans',-apple-system,sans-serif" }}>
      <div style={{ padding:"0 0 24px" }}>

        {/* Stats */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, marginBottom:14 }}>
          {[{label:"Weeks pregnant",value:"20",sub:"Week 20, day 3"},{label:"Days to due date",value:"140",sub:"~Aug 10, 2026"},{label:"Trimester",value:"2nd",sub:"Weeks 14–27"}].map(s=>(
            <div key={s.label} style={{ background:C.sand, borderRadius:C.r, padding:"12px 14px" }}>
              <div style={{ fontSize:10, color:C.muted, marginBottom:4 }}>{s.label}</div>
              <div style={{ fontSize:22, fontWeight:700, color:C.text, letterSpacing:"-.02em" }}>{s.value}</div>
              <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>{s.sub}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display:"flex", borderBottom:`1px solid ${C.border}`, marginBottom:14 }}>
          {[["thisweek","This week"],["milestones","Milestones"],["birthplan","Birth plan"],["daily","Daily log"],["meds","Medications"],["reminders","Reminders"]].map(([id,label])=>(
            <button key={id} onClick={()=>setTab(id)} style={{ fontFamily:"inherit", fontSize:12, fontWeight:600, color:tab===id?C.primaryDk:C.muted, background:"none", border:"none", borderBottom:`2px solid ${tab===id?C.primary:"transparent"}`, padding:"10px 14px", cursor:"pointer", whiteSpace:"nowrap" }}>{label}</button>
          ))}
        </div>

        {/* THIS WEEK */}
        {tab==="thisweek" && (
          <div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
              <Card>
                <SLabel>Your baby this week</SLabel>
                <div style={{ fontSize:32, marginBottom:8 }}>🍌</div>
                <div style={{ fontSize:13.5, fontWeight:700, color:C.text, marginBottom:6 }}>The size of a banana</div>
                <div style={{ fontSize:12, color:C.text2, lineHeight:1.65 }}>{week.baby}</div>
              </Card>
              <Card>
                <SLabel>Your body this week</SLabel>
                <div style={{ fontSize:32, marginBottom:8 }}>🤰</div>
                <div style={{ fontSize:12, color:C.text2, lineHeight:1.65, marginBottom:10 }}>{week.you}</div>
                <div style={{ background:C.primaryL, borderRadius:10, padding:"8px 12px", fontSize:12, color:C.primaryDk }}>
                  <strong>This week's focus:</strong> {week.focus}
                </div>
              </Card>
            </div>
            <Card>
              <SLabel>Anatomy scan — what to expect today</SLabel>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <div>
                  <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:8 }}>What they check</div>
                  {["Baby's heartbeat and movement","Brain, spine, and skull development","Heart chambers and major vessels","Kidneys, bladder, stomach","Limbs and face structure","Placenta placement and umbilical cord","Amniotic fluid levels"].map(i=>(
                    <div key={i} style={{ display:"flex", gap:7, fontSize:12, color:C.text2, padding:"3px 0", alignItems:"baseline" }}>
                      <span style={{ color:C.primary, fontWeight:700, flexShrink:0 }}>·</span>{i}
                    </div>
                  ))}
                </div>
                <div>
                  <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:8 }}>Questions to ask</div>
                  {["Is the placenta position normal?","Is the growth on track for my dates?","Can I see the cord insertion point?","Are all four heart chambers visible?","Should I schedule a follow-up scan?"].map(i=>(
                    <div key={i} style={{ display:"flex", gap:7, fontSize:12, color:C.text2, padding:"3px 0", alignItems:"baseline" }}>
                      <span style={{ color:C.primary, fontWeight:700, flexShrink:0 }}>·</span>{i}
                    </div>
                  ))}
                  <button style={{ marginTop:10, fontFamily:"inherit", fontSize:11, fontWeight:600, color:C.primaryDk, background:C.primaryL, border:`1px solid ${C.primaryM}`, borderRadius:999, padding:"6px 14px", cursor:"pointer", width:"100%" }}>Ask Nova to prep me →</button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* MILESTONES */}
        {tab==="milestones" && (
          <div>
            <Card>
              <SLabel>Pregnancy milestones — week 20 of 40</SLabel>
              <div style={{ marginBottom:12 }}>
                <div style={{ height:8, background:C.sand2, borderRadius:999, overflow:"hidden" }}>
                  <div style={{ width:"50%", height:"100%", background:C.primary, borderRadius:999 }} />
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:C.muted, marginTop:4 }}>
                  <span>Week 1</span><span style={{ fontWeight:700, color:C.primary }}>Week 20 — halfway!</span><span>Week 40</span>
                </div>
              </div>
              {MILESTONES.map((m,i)=>(
                <div key={i} style={{ display:"flex", gap:11, padding:"9px 0", borderBottom:i<MILESTONES.length-1?`1px solid ${C.sand2}`:"none" }}>
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"center" }}>
                    <div style={{ width:22, height:22, borderRadius:"50%", background:m.current?C.primary:m.done?C.primaryL:"#e8e8e8", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:700, color:m.current?"#fff":m.done?C.primaryDk:C.muted, flexShrink:0 }}>
                      {m.current?"→":m.done?"✓":m.n}
                    </div>
                    {i<MILESTONES.length-1 && <div style={{ width:1, flex:1, background:C.border, margin:"2px 0" }} />}
                  </div>
                  <div style={{ flex:1, paddingTop:2 }}>
                    <div style={{ fontSize:12, fontWeight:700, color:m.current?C.primary:m.done?C.text:C.muted }}>{m.label}</div>
                    <div style={{ fontSize:11, color:C.muted, marginTop:1 }}>{m.date}</div>
                  </div>
                  {m.current && <Tag>Today</Tag>}
                </div>
              ))}
            </Card>
          </div>
        )}

        {/* BIRTH PLAN */}
        {tab==="birthplan" && (
          <div>
            <Card>
              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:12 }}>
                <SLabel style={{margin:0}}>Birth plan builder</SLabel>
                <Btn variant="ghost" style={{fontSize:11}}>Download PDF →</Btn>
              </div>
              <div style={{ background:C.primaryL, borderRadius:C.r, padding:"10px 14px", marginBottom:14, fontSize:12, color:C.primaryDk, lineHeight:1.5 }}>
                Nova will turn your answers into a document your birth team can actually use. Answer at your own pace — she'll remind you to finish before week 36.
              </div>
              {[
                { section:"Pain management", q:"What's your preference?", options:["Epidural","Natural/unmedicated","I'm open — see how I feel","Nitrous oxide if available","IV pain medication only"] },
                { section:"Labor support", q:"Who do you want in the room?", options:["Partner only","Partner + family member","Partner + doula","Doula only","I haven't decided"] },
                { section:"Delayed cord clamping", q:"Your preference?", options:["Yes — wait at least 60 seconds","Yes — wait until cord stops pulsing","No strong preference","I'd like to discuss with my provider"] },
                { section:"Skin-to-skin", q:"Immediately after birth?", options:["Yes — immediate skin-to-skin","Yes if baby is healthy","C-section skin-to-skin (if applicable)","I need guidance on this"] },
              ].map((item,i)=>(
                <div key={i} style={{ marginBottom:14, paddingBottom:14, borderBottom:`1px solid ${C.sand2}` }}>
                  <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:2 }}>{item.section}</div>
                  <div style={{ fontSize:12, color:C.muted, marginBottom:8 }}>{item.q}</div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {item.options.map(o=>(
                      <button key={o} style={{ fontFamily:"inherit", fontSize:11, fontWeight:600, padding:"6px 13px", borderRadius:C.r, border:`1px solid ${C.border}`, background:C.sand, color:C.muted, cursor:"pointer" }}>{o}</button>
                    ))}
                  </div>
                </div>
              ))}
              <Btn variant="primary" style={{width:"100%"}}>Save birth plan</Btn>
            </Card>
          </div>
        )}

        {/* DAILY LOG */}
        {tab==="daily" && (
          <div>
            <Card>
              <SLabel>📝 Today's log — Week 20, Day 3</SLabel>
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:12, fontWeight:600, color:C.text, marginBottom:8 }}>1. How are you feeling today?</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {MOODS.map((emoji,i)=>(
                    <button key={i} onClick={()=>setMood(i)} style={{ background:mood===i?C.primaryL:C.sand, border:`1px solid ${mood===i?C.primaryM:C.border}`, borderRadius:10, padding:"8px 10px", cursor:"pointer", textAlign:"center", minWidth:52 }}>
                      <div style={{ fontSize:20 }}>{emoji}</div>
                      <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>{MOOD_LABELS[i]}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:12, fontWeight:600, color:C.text, marginBottom:8 }}>2. Symptoms</div>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                  {SYMPTOMS.map(s=>(
                    <button key={s} onClick={()=>toggleSym(s)} style={{ fontSize:11, fontWeight:600, padding:"5px 11px", borderRadius:999, border:`1px solid ${symptoms.includes(s)?C.primaryM:C.border}`, background:symptoms.includes(s)?C.primaryL:C.sand, color:symptoms.includes(s)?C.primaryDk:C.muted, cursor:"pointer" }}>{s}</button>
                  ))}
                </div>
              </div>
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:12, fontWeight:600, color:C.text, marginBottom:8 }}>3. Baby movements (after week 18)</div>
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <div style={{ display:"flex", gap:6 }}>
                    {["None yet","A few flutters","Regular kicks","Very active"].map(o=>(
                      <button key={o} style={{ fontFamily:"inherit", fontSize:11, fontWeight:600, padding:"6px 12px", borderRadius:C.r, border:`1px solid ${C.border}`, background:C.sand, color:C.muted, cursor:"pointer" }}>{o}</button>
                    ))}
                  </div>
                </div>
              </div>
              <Btn variant="primary" style={{width:"100%",padding:"10px"}}>Save today's log</Btn>
            </Card>
          </div>
        )}

        {/* MEDICATIONS */}
        {tab==="meds" && (
          <div>
            <Card>
              <SLabel>💊 Prenatal medications & supplements</SLabel>
              {[
                { name:"Prenatal vitamin", dose:"1 capsule daily", timing:"Morning with food", reminder:"8:00 AM" },
                { name:"Folate 400mcg", dose:"400mcg",            timing:"Daily",            reminder:"8:00 AM" },
                { name:"Iron 27mg",      dose:"27mg",             timing:"With vitamin C",   reminder:"12:00 PM" },
                { name:"Baby aspirin",   dose:"81mg",             timing:"Nightly",          reminder:"9:00 PM", note:"As prescribed — pre-eclampsia prevention" },
              ].map((m,i)=>(
                <div key={i} style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", background:C.sand, borderRadius:C.r, marginBottom:8 }}>
                  <div style={{ width:36, height:36, borderRadius:"50%", background:C.primaryL, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, flexShrink:0 }}>💊</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{m.name}</div>
                    <div style={{ fontSize:11, color:C.muted }}>{m.dose} · {m.timing}</div>
                    {m.note && <div style={{ fontSize:11, color:C.am, marginTop:2 }}>{m.note}</div>}
                    <div style={{ fontSize:11, color:C.primary, marginTop:2 }}>🔔 {m.reminder}</div>
                  </div>
                  <button style={{ fontFamily:"inherit", fontSize:11, color:C.muted, background:"none", border:`1px solid ${C.border}`, borderRadius:999, padding:"3px 10px", cursor:"pointer" }}>Edit</button>
                </div>
              ))}
              <Btn variant="ghost" style={{width:"100%",marginTop:4}}>+ Add medication</Btn>
            </Card>
            <Card>
              <SLabel>What to avoid in pregnancy</SLabel>
              {[["❌ Avoid","High-mercury fish (shark, swordfish, tilefish), raw sushi, deli meat, unpasteurised cheese, alcohol, excess caffeine (>200mg/day)"],["⚠️ Check first","All supplements, herbal teas, new medications — even OTC. Ask Nova or your OB before starting anything new."],["✓ Safe","Tylenol (paracetamol) for pain — avoid ibuprofen and aspirin unless prescribed."]].map(([icon,text])=>(
                <div key={icon} style={{ display:"flex", gap:8, padding:"8px 0", borderBottom:`1px solid ${C.sand2}`, fontSize:12, color:C.text2, alignItems:"baseline", lineHeight:1.5 }}>
                  <span style={{ flexShrink:0 }}>{icon}</span>{text}
                </div>
              ))}
            </Card>
          </div>
        )}

        {/* REMINDERS */}
        {tab==="reminders" && (
          <div>
            <Card>
              <SLabel>⏰ Upcoming appointments</SLabel>
              <div style={{ background:C.primaryL, border:`1px solid ${C.primaryM}`, borderRadius:C.r, padding:"10px 14px", marginBottom:14, fontSize:12, color:C.primaryDk }}>
                <strong>Today — Anatomy scan</strong> · 2:30 PM · Wexner Medical OB · Bring your partner if you'd like to find out the sex. Allow 45–60 min.
              </div>
              {[
                { week:"Week 24", appt:"OB check-up + glucose screening (GD test)", status:"upcoming" },
                { week:"Week 28", appt:"Rh blood typing if needed · 3rd trimester starts", status:"upcoming" },
                { week:"Week 32", appt:"Growth scan (if indicated) · kick count starts", status:"upcoming" },
                { week:"Week 36", appt:"GBS swab · weekly appointments begin", status:"upcoming" },
                { week:"Week 39+",appt:"Induction discussion if going past 40 weeks", status:"upcoming" },
              ].map((a,i)=>(
                <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"9px 0", borderBottom:`1px solid ${C.sand2}` }}>
                  <Tag bg={C.sand2} color={C.muted} border={C.border}>{a.week}</Tag>
                  <div style={{ flex:1, fontSize:12, color:C.text2 }}>{a.appt}</div>
                  <button style={{ fontFamily:"inherit", fontSize:11, color:C.muted, background:"none", border:`1px solid ${C.border}`, borderRadius:999, padding:"3px 10px", cursor:"pointer" }}>+ Add reminder</button>
                </div>
              ))}
            </Card>
          </div>
        )}

      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// PREGNANCY HEALTH RECORD
// ══════════════════════════════════════════════════════════════════════
export function PregnancyHealthRecord({ navigate }) {
  const nav = navigate || (() => {});
  return (
    <div style={{ fontFamily:"'Plus Jakarta Sans',-apple-system,sans-serif" }}>
      <div style={{ padding:"0 0 24px" }}>
        <NovaBanner text="All your prenatal labs are in range. Your anatomy scan is today at 2:30 PM — Nova has prepared what to expect and what to ask Dr. Martinez." prompts={["What does my HCG level mean?","Is my weight gain on track?","Prep me for the anatomy scan"]} />

        {/* Prenatal labs */}
        <SLabel>Prenatal labs — overview</SLabel>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:14 }}>
          {[
            { label:"HCG (early)", value:"62,000", sub:"mIU/mL · Week 8", color:C.primary, note:"Normal" },
            { label:"Blood type", value:"A+",      sub:"Rh positive",      color:C.text,   note:"No Rh concerns" },
            { label:"Hemoglobin", value:"11.8",    sub:"g/dL · normal",    color:C.primary,note:"In range" },
            { label:"NT measure", value:"1.4mm",   sub:"Week 12 · normal", color:C.primary,note:"Low-risk" },
          ].map(s=>(
            <div key={s.label} style={{ background:C.sand, borderRadius:C.r, padding:"12px 14px" }}>
              <div style={{ fontSize:10, color:C.muted, marginBottom:3 }}>{s.label}</div>
              <div style={{ fontSize:16, fontWeight:700, color:s.color }}>{s.value}</div>
              <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>{s.sub}</div>
              <span style={{ fontSize:10, fontWeight:600, padding:"1px 7px", borderRadius:999, background:C.primaryL, color:C.primaryDk, border:`1px solid ${C.primaryM}`, marginTop:4, display:"inline-block" }}>{s.note}</span>
            </div>
          ))}
        </div>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
          <Card>
            <SLabel>Lab history</SLabel>
            {[
              { name:"NIPT screen",         result:"Low risk all aneuploidies",  date:"Nov 20, 2025", ok:true },
              { name:"First trimester screen",result:"1:5,000 Down syndrome risk",date:"Nov 8, 2025",  ok:true },
              { name:"Thyroid (TSH)",        result:"2.1 mIU/L (normal)",        date:"Oct 15, 2025", ok:true },
              { name:"Rubella immunity",     result:"Immune",                    date:"Oct 15, 2025", ok:true },
              { name:"Glucose screening",    result:"Not yet (week 24–28)",      date:"—",            ok:null },
              { name:"GBS swab",             result:"Not yet (week 36)",         date:"—",            ok:null },
            ].map((l,i)=>(
              <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:`1px solid ${C.sand2}`, fontSize:12 }}>
                <div>
                  <div style={{ fontWeight:700, color:C.text }}>{l.name}</div>
                  <div style={{ color:l.ok===null?C.muted:C.text2, marginTop:1 }}>{l.result}</div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ color:C.muted, fontSize:11 }}>{l.date}</div>
                  {l.ok!==null && <span style={{ fontSize:10, fontWeight:600, padding:"1px 7px", borderRadius:999, background:C.primaryL, color:C.primaryDk, border:`1px solid ${C.primaryM}`, marginTop:3, display:"inline-block" }}>✓</span>}
                </div>
              </div>
            ))}
          </Card>
          <Card>
            <SLabel>Weight & vitals trend</SLabel>
            {[
              { week:"Week 8",  weight:"142 lb", bp:"118/72", note:"Baseline" },
              { week:"Week 12", weight:"144 lb", bp:"116/70", note:"+2 lb" },
              { week:"Week 16", weight:"147 lb", bp:"114/68", note:"+5 lb" },
              { week:"Week 20", weight:"151 lb", bp:"112/70", note:"+9 lb", current:true },
            ].map((v,i)=>(
              <div key={i} style={{ display:"flex", gap:10, padding:"8px 0", borderBottom:`1px solid ${C.sand2}`, fontSize:12 }}>
                <Tag bg={v.current?C.primaryL:C.sand2} color={v.current?C.primaryDk:C.muted} border={v.current?C.primaryM:C.border}>{v.week}</Tag>
                <div style={{ flex:1 }}>
                  <span style={{ fontWeight:600, color:C.text }}>{v.weight}</span>
                  <span style={{ color:C.muted }}> · BP {v.bp}</span>
                </div>
                <span style={{ color:v.current?C.primary:C.muted, fontSize:11 }}>{v.note}</span>
              </div>
            ))}
            <div style={{ fontSize:11, color:C.muted, marginTop:8, lineHeight:1.5 }}>Expected total gain: 25–35 lb (normal BMI). You're at +9 lb at week 20 — right on track.</div>
          </Card>
        </div>

        <Card>
          <SLabel>Upcoming tests & what they mean</SLabel>
          {[
            { name:"Glucose screening (GD test)", when:"Week 24–28", what:"1-hour drink test. Screens for gestational diabetes. If positive, a 3-hour confirmatory test follows.", prep:"No special prep for 1-hour. Fast before 3-hour if needed." },
            { name:"GBS swab",                    when:"Week 36",    what:"Vaginal/rectal swab for Group B Strep. If positive, you'll get antibiotics in labor — routine and safe.", prep:"No prep needed. Quick swab at your regular appointment." },
          ].map((t,i)=>(
            <div key={i} style={{ padding:"10px 0", borderBottom:`1px solid ${C.sand2}` }}>
              <div style={{ display:"flex", gap:8, alignItems:"flex-start", marginBottom:5 }}>
                <Tag bg={C.sand2} color={C.muted} border={C.border}>{t.when}</Tag>
                <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{t.name}</div>
              </div>
              <div style={{ fontSize:12, color:C.text2, lineHeight:1.5, marginBottom:4 }}>{t.what}</div>
              <div style={{ fontSize:11, color:C.primaryDk, background:C.primaryL, borderRadius:8, padding:"6px 10px" }}>📋 {t.prep}</div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// PREGNANCY COMMUNITY
// ══════════════════════════════════════════════════════════════════════
export function PregnancyCommunity({ navigate }) {
  const nav = navigate || (() => {});
  const POSTS = [
    { init:"A", bg:C.primaryM, color:C.primaryDk, name:"Ava K.",  badge:"week 20", date:"3/23/2026", body:"Anatomy scan in 2 hours and I'm a nervous wreck. Anyone else feel more anxious for this one than the 12-week? Somehow finding out everything is okay makes it more real that something could not be. 🤍", hearts:78, boats:52, replies:14 },
    { init:"S", bg:C.ppM,      color:C.ppD,       name:"Sofia M.",badge:null,      date:"3/23/2026", body:"Week 28 and officially in the third trimester! Baby is kicking so much now that my husband can feel it from the outside. This second trimester has been the best part so far. 🤰", hearts:134, boats:31, replies:9 },
    { init:"M", bg:C.pinkM,    color:C.pinkDk,    name:"Maya",   badge:"friend",   date:"3/23/2026", body:"The anatomy scan was incredible. We got to see every little part of them — fingers, the four chambers of the heart, little nose in profile. I cried the whole time. This pregnancy community has made every week better.", hearts:189, boats:44, replies:22 },
  ];
  return (
    <div style={{ fontFamily:"'Plus Jakarta Sans',-apple-system,sans-serif" }}>
      <div style={{ padding:"12px 16px 6px", background:C.sand, borderBottom:`1px solid ${C.border}` }}>
        <div style={{ fontSize:10, fontWeight:700, letterSpacing:".1em", textTransform:"uppercase", color:C.muted, marginBottom:2 }}>Community · Pregnancy</div>
        <div style={{ fontSize:16, fontWeight:700, color:C.text, marginBottom:2 }}>Your pregnancy community</div>
        <div style={{ fontSize:12, color:C.muted, marginBottom:10 }}>🤰 Pregnancy chapter — 11,200 members · 118 online right now</div>
      </div>
      <div style={{ padding:14 }}>
        <NovaBanner text="Your anatomy scan is today — 14 women in the 'Week 20' room are sharing their scan experiences right now. Join them." prompts={["Who's having their anatomy scan this week?","What's the high risk community?","Find pregnancy friends near me"]} />
        <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:C.r, padding:"12px 14px", marginBottom:12 }}>
          <div style={{ display:"flex", gap:10 }}>
            <div style={{ width:36, height:36, borderRadius:"50%", background:C.primaryM, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, color:C.primaryDk }}>M</div>
            <input placeholder="Share something with your chapter..." style={{ flex:1, fontFamily:"inherit", fontSize:13, border:`1px solid ${C.border}`, borderRadius:999, padding:"8px 14px", background:C.sand, color:C.muted }} />
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", marginTop:10 }}>
            <div style={{ display:"flex", gap:6 }}>
              <Btn variant="sand" style={{fontSize:11,padding:"4px 12px"}}>📷 Photo</Btn>
              <Btn variant="sand" style={{fontSize:11,padding:"4px 12px"}}>🎥 Video</Btn>
            </div>
            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <Tag>🤰 Pregnancy</Tag>
              <Btn variant="primary" style={{fontSize:12,padding:"6px 16px"}}>Post</Btn>
            </div>
          </div>
        </div>
        {POSTS.map((post,i)=>(
          <div key={i} style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:C.r, padding:"13px 15px", marginBottom:10 }}>
            <div style={{ display:"flex", gap:9, marginBottom:8 }}>
              <div style={{ width:36, height:36, borderRadius:"50%", background:post.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, color:post.color, flexShrink:0 }}>{post.init}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:13, fontWeight:700, color:C.text }}>
                  {post.name}
                  {post.badge && <span style={{ fontSize:10, fontWeight:600, padding:"1px 7px", borderRadius:999, background:C.primaryL, color:C.primaryDk, border:`1px solid ${C.primaryM}`, marginLeft:6 }}>{post.badge}</span>}
                </div>
                <div style={{ fontSize:11, color:C.muted }}>{post.date}</div>
              </div>
            </div>
            <div style={{ fontSize:13, color:C.text2, lineHeight:1.6, marginBottom:10 }}>{post.body}</div>
            <div style={{ display:"flex", borderTop:`1px solid ${C.sand2}`, paddingTop:7 }}>
              <button style={{ fontFamily:"inherit", fontSize:12, fontWeight:600, color:C.primary, background:"none", border:"none", flex:1, cursor:"pointer" }}>❤️ {post.hearts}</button>
              <button style={{ fontFamily:"inherit", fontSize:12, fontWeight:600, color:C.primaryDk, background:"none", border:"none", flex:1, cursor:"pointer" }}>🤝 Same boat · {post.boats}</button>
              <button style={{ fontFamily:"inherit", fontSize:12, fontWeight:600, color:C.muted, background:"none", border:"none", flex:1, cursor:"pointer" }}>💬 Reply · {post.replies}</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// PREGNANCY CONNECT
// ══════════════════════════════════════════════════════════════════════
export function PregnancyConnect({ navigate }) {
  const nav = navigate || (() => {});
  const [tab, setTab] = useState("women");
  return (
    <div style={{ fontFamily:"'Plus Jakarta Sans',-apple-system,sans-serif" }}>
      <div style={{ display:"flex", borderBottom:`1px solid ${C.border}` }}>
        {[["women","💕 Women like me"],["rooms","🟢 Live rooms"],["groups","👥 Groups"]].map(([id,label])=>(
          <button key={id} onClick={()=>setTab(id)} style={{ fontFamily:"inherit", fontSize:12, fontWeight:600, color:tab===id?C.primaryDk:C.muted, background:"none", border:"none", borderBottom:`2px solid ${tab===id?C.primary:"transparent"}`, padding:"11px 16px", cursor:"pointer", whiteSpace:"nowrap" }}>{label}</button>
        ))}
      </div>
      <div style={{ padding:14 }}>
        {tab==="women" && (
          <div>
            <div style={{ background:C.primaryL, border:`1px solid ${C.primaryM}`, borderRadius:C.r, padding:"14px 16px", marginBottom:14 }}>
              <div style={{ fontSize:16, fontWeight:700, color:C.primaryDk, marginBottom:3 }}>Women going through this with you</div>
              <div style={{ fontSize:12, color:C.primaryDk }}>Nova matched you on week 20 · anatomy scan week · Columbus, OH</div>
            </div>
            <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:14 }}>
              {["All matches","Same week","Due same month","Same city","IVF graduates","High risk"].map(f=>(
                <button key={f} style={{ fontFamily:"inherit", fontSize:11, fontWeight:600, padding:"4px 12px", borderRadius:999, border:`1px solid ${f==="All matches"?C.primaryM:C.border}`, background:f==="All matches"?C.primaryL:C.sand, color:f==="All matches"?C.primaryDk:C.muted, cursor:"pointer" }}>{f}</button>
              ))}
            </div>
            <div style={{ fontSize:10, fontWeight:700, letterSpacing:".1em", textTransform:"uppercase", color:C.muted, marginBottom:10 }}>Your closest match</div>
            <div style={{ background:C.white, border:`2px solid ${C.primary}`, borderRadius:C.r, padding:16, marginBottom:10 }}>
              <div style={{ display:"flex", gap:10, marginBottom:8 }}>
                <div style={{ width:40, height:40, borderRadius:"50%", background:C.primaryM, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, color:C.primaryDk }}>AK</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text }}>Ava K.</div>
                  <div style={{ fontSize:11, color:C.muted }}>Week 20 · Due Aug 2026 · IVF graduate · Columbus</div>
                </div>
                <span style={{ fontSize:11, fontWeight:700, padding:"2px 8px", borderRadius:999, background:C.primaryL, color:C.primaryDk, border:`1px solid ${C.primaryM}` }}>96% match</span>
              </div>
              <div style={{ display:"flex", gap:5, marginBottom:10, flexWrap:"wrap" }}>
                <Tag>Same week</Tag>
                <Tag bg={C.pinkL} color={C.pinkDk} border={C.pinkM}>IVF graduate</Tag>
                <Tag bg={C.ppL} color={C.ppD} border={C.ppM}>Same city</Tag>
              </div>
              <div style={{ fontStyle:"italic", fontSize:12, color:C.text2, background:C.sand, borderRadius:8, padding:"8px 12px", borderLeft:`3px solid ${C.primaryM}`, marginBottom:12 }}>"Anatomy scan today — same as you! So nervous and excited. After IVF I still can't believe we're here at week 20."</div>
              <div style={{ display:"flex", gap:8 }}>
                <Btn variant="primary" style={{flex:1}}>Connect</Btn>
                <Btn variant="ghost" style={{flex:1}}>Message</Btn>
              </div>
            </div>
            {[
              { init:"SL", bg:C.ppM, color:C.ppD, name:"Sofia L.", sub:"Week 21 · high risk · Cincinnati", pct:"91%", tags:["Near due date","High risk"] },
              { init:"JM", bg:"#b5d4f4", color:"#185fa5", name:"Jamie M.", sub:"Week 19 · first time mom · Columbus", pct:"88%", tags:["First time mom","Same city"] },
            ].map((m,i)=>(
              <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"11px 12px", background:C.white, border:`1px solid ${C.border}`, borderRadius:C.r, marginBottom:8 }}>
                <div style={{ width:36, height:36, borderRadius:"50%", background:m.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:12, fontWeight:700, color:m.color }}>{m.init}</div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:C.text }}>{m.name} <span style={{ fontSize:10, fontWeight:600, padding:"1px 7px", borderRadius:999, background:C.primaryL, color:C.primaryDk, border:`1px solid ${C.primaryM}` }}>{m.pct}</span></div>
                  <div style={{ fontSize:11, color:C.muted }}>{m.sub}</div>
                  <div style={{ display:"flex", gap:4, marginTop:5 }}>{m.tags.map(t=><Tag key={t}>{t}</Tag>)}</div>
                </div>
                <Btn variant="primary" style={{fontSize:11,padding:"5px 14px"}}>Connect</Btn>
              </div>
            ))}
          </div>
        )}
        {tab==="rooms" && (
          <div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
              {[
                { name:"Anatomy scan week", desc:"Sharing scans, nerves, and good news", count:14, hot:true },
                { name:"Second trimester wins", desc:"Energy back, bump growing, feeling good", count:22 },
                { name:"Nausea & first trimester", desc:"When does it end? Coping strategies", count:8 },
                { name:"High-risk pregnancy", desc:"MFM, monitoring, NICU prep, support", count:11 },
                { name:"IVF graduates pregnant", desc:"Navigating pregnancy after fertility treatment", count:9 },
                { name:"Birth plan questions", desc:"Epidural vs natural, doulas, hospital policies", count:16 },
              ].map(room=>(
                <div key={room.name} style={{ background:room.hot?C.primaryL:C.white, border:`1px solid ${room.hot?C.primaryM:C.border}`, borderRadius:C.r, padding:"13px 14px" }}>
                  {room.hot && <span style={{ fontSize:10, fontWeight:700, background:C.primary, color:"#fff", padding:"2px 7px", borderRadius:999, display:"inline-block", marginBottom:6 }}>Active now</span>}
                  <div style={{ fontSize:13, fontWeight:700, color:room.hot?C.primaryDk:C.text, marginBottom:4 }}>{room.name}</div>
                  <div style={{ fontSize:11, color:room.hot?C.primaryDk:C.muted, marginBottom:10, lineHeight:1.4 }}>{room.desc}</div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:11, fontWeight:600, color:C.primaryDk }}><div style={{ width:6, height:6, borderRadius:"50%", background:"#22c55e" }} />{room.count} online</div>
                    <Btn variant={room.hot?"primary":"sand"} style={{fontSize:11,padding:"4px 12px"}}>Join</Btn>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {tab==="groups" && (
          <div>
            {[
              { icon:"🤰", name:"Due in Summer 2026", meta:"1,180 members · your due date", joined:true },
              { icon:"🌸", name:"IVF Graduates — Pregnant", meta:"920 members · IVF to pregnancy", joined:true },
              { icon:"💙", name:"High-Risk Pregnancy Circle", meta:"1,400 members", joined:false },
              { icon:"👶", name:"First-Time Moms", meta:"2,800 members · every question welcome", joined:false },
              { icon:"🤰", name:"Pregnancy After Loss", meta:"1,100 members · extra tender space", joined:false },
            ].map(g=>(
              <div key={g.name} style={{ display:"flex", alignItems:"center", gap:10, padding:"11px 12px", background:C.white, border:`1px solid ${C.border}`, borderRadius:C.r, marginBottom:8 }}>
                <div style={{ width:38, height:38, borderRadius:10, background:C.primaryL, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>{g.icon}</div>
                <div style={{ flex:1 }}><div style={{ fontSize:12, fontWeight:700, color:C.text }}>{g.name}</div><div style={{ fontSize:11, color:C.muted }}>{g.meta}</div></div>
                {g.joined?<span style={{ fontSize:10, fontWeight:600, padding:"2px 8px", borderRadius:999, background:C.primaryL, color:C.primaryDk, border:`1px solid ${C.primaryM}` }}>Joined</span>:<Btn variant="ghost" style={{fontSize:11,padding:"4px 12px"}}>Join →</Btn>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// PREGNANCY EVENTS
// ══════════════════════════════════════════════════════════════════════
export function PregnancyEvents({ navigate }) {
  const nav = navigate || (() => {});
  return (
    <div style={{ fontFamily:"'Plus Jakarta Sans',-apple-system,sans-serif" }}>
      <div style={{ padding:"0 0 24px" }}>
        <NovaBanner text="You're in week 20 — Nova found 6 pregnancy events near Columbus this month, including a childbirth prep class starting next week and a free hospital tour." prompts={["Find doula meetups near me","Virtual birth class options","Breastfeeding prep classes"]} />
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:14 }}>
          {["All","In person","Virtual","This week","This month"].map(f=>(
            <button key={f} style={{ fontFamily:"inherit", fontSize:11, fontWeight:600, padding:"5px 13px", borderRadius:999, border:`1px solid ${f==="All"?C.primaryM:C.border}`, background:f==="All"?C.primaryL:C.sand, color:f==="All"?C.primaryDk:C.muted, cursor:"pointer" }}>{f}</button>
          ))}
        </div>
        <div style={{ fontSize:10, fontWeight:700, letterSpacing:".1em", textTransform:"uppercase", color:C.muted, marginBottom:10 }}>This week</div>
        {[
          { date:"MAR\n25", title:"Wexner Medical OB — Hospital Tour", org:"Ohio State Wexner Medical · Columbus", desc:"Free 60-minute tour of labor & delivery, NICU, and postpartum unit. Bring your birth partner. Ideal weeks 20–32.", tags:["In person","Free","Nova matched"], hot:true },
          { date:"MAR\n27", title:"Childbirth Prep — Lamaze Series (4-week)", org:"Columbus Birth Center", desc:"Comprehensive 4-week course. Pain management, stages of labor, epidural vs natural, recovery. Starts this Thursday.", tags:["In person","$180 for series","Weeks 20–32"] },
        ].map((ev,i)=>(
          <div key={i} style={{ background:ev.hot?C.primaryL:C.white, border:`1px solid ${ev.hot?C.primaryM:C.border}`, borderRadius:C.r, padding:16, marginBottom:10, display:"flex", gap:14 }}>
            <div style={{ flexShrink:0, textAlign:"center", background:ev.hot?C.primary:C.sand, color:ev.hot?"#fff":C.text, borderRadius:10, padding:"8px 12px", minWidth:52 }}>
              {ev.date.split("\n").map((l,j)=><div key={j} style={{ fontSize:j===0?10:20, fontWeight:700, lineHeight:1.1 }}>{l}</div>)}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:5 }}>{ev.tags.map(t=><Tag key={t} bg={t==="Nova matched"?C.ppL:C.sand2} color={t==="Nova matched"?C.ppD:C.muted} border={t==="Nova matched"?C.ppM:C.border}>{t}</Tag>)}</div>
              <div style={{ fontSize:13, fontWeight:700, color:ev.hot?C.primaryDk:C.text, marginBottom:3 }}>{ev.title}</div>
              <div style={{ fontSize:11, color:C.muted, marginBottom:6 }}>{ev.org}</div>
              <div style={{ fontSize:12, color:ev.hot?C.primaryDk:C.text2, lineHeight:1.5 }}>{ev.desc}</div>
            </div>
            <Btn variant={ev.hot?"primary":"ghost"} style={{alignSelf:"flex-end",whiteSpace:"nowrap"}}>{ev.hot?"Register →":"Register"}</Btn>
          </div>
        ))}
        <div style={{ fontSize:10, fontWeight:700, letterSpacing:".1em", textTransform:"uppercase", color:C.muted, margin:"14px 0 10px" }}>Coming up</div>
        {[
          { date:"APR\n5",  title:"Breastfeeding Prep Class", org:"Nationwide Children's · Columbus", desc:"Expert-led. Latch, supply, pumping, returning to work. Bring your partner for the partner module.", tags:["In person","$40"] },
          { date:"APR\n8",  title:"Birth & Beyond Virtual Summit", org:"OrivonAI · Virtual", desc:"Live speakers: OB, midwife, doula, and a pelvic floor PT. Q&A included. Free for OrivonAI members.", tags:["Virtual","Free","OrivonAI"] },
          { date:"APR\n15", title:"Newborn Care & Safety Basics", org:"Columbus Mom Network", desc:"CPR for infants, safe sleep, car seat installation, newborn feeding — all in one evening.", tags:["In person","$30","Columbus OH"] },
        ].map((ev,i)=>(
          <div key={i} style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:C.r, padding:16, marginBottom:8, display:"flex", gap:14 }}>
            <div style={{ flexShrink:0, textAlign:"center", background:C.sand, color:C.text, borderRadius:10, padding:"8px 12px", minWidth:52 }}>
              {ev.date.split("\n").map((l,j)=><div key={j} style={{ fontSize:j===0?10:20, fontWeight:700, lineHeight:1.1 }}>{l}</div>)}
            </div>
            <div style={{ flex:1 }}>
              <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:5 }}>{ev.tags.map(t=><Tag key={t} bg={C.sand2} color={C.muted} border={C.border}>{t}</Tag>)}</div>
              <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:3 }}>{ev.title}</div>
              <div style={{ fontSize:11, color:C.muted, marginBottom:6 }}>{ev.org}</div>
              <div style={{ fontSize:12, color:C.text2, lineHeight:1.5 }}>{ev.desc}</div>
            </div>
            <Btn variant="ghost" style={{alignSelf:"flex-end"}}>Register</Btn>
          </div>
        ))}
        <div style={{ background:C.primary, borderRadius:C.r, padding:"16px 20px", display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
          <div>
            <div style={{ fontSize:13, fontWeight:600, color:"#fff" }}>Submit a pregnancy event near you</div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.8)", marginTop:2 }}>Birth classes, hospital tours, support groups, doula meetups</div>
          </div>
          <button style={{ fontFamily:"inherit", fontSize:12, fontWeight:700, color:C.primaryDk, background:"#fff", border:"none", borderRadius:999, padding:"8px 18px", cursor:"pointer", whiteSpace:"nowrap" }}>+ Submit event</button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// PREGNANCY PRODUCTS
// ══════════════════════════════════════════════════════════════════════
export function PregnancyProducts({ navigate }) {
  const nav = navigate || (() => {});
  const [filter, setFilter] = useState("all");
  const PRODUCTS = [
    { brand:"Ritual", name:"Essential Prenatal Multivitamin", desc:"OB-recommended. Methylfolate not folic acid. DHA, D3, iron. Delayed-release to reduce nausea.", price:"$35", tag:"Vitamins", tagType:"vitamins", digs:1247, rating:4.9, reviews:"14.2k", featured:true },
    { brand:"Snoogle", name:"Snoogle Original Pregnancy Pillow", desc:"The classic C-shaped pillow. Supports belly, back, and knees simultaneously. Most-loved in the community.", price:"$70", tag:"Comfort", tagType:"comfort", digs:1089, rating:4.8, reviews:"22k" },
    { brand:"Doppler", name:"Sonoline B Fetal Doppler", desc:"Hear baby's heartbeat at home from week 12+. Doctor-grade 3MHz probe. Peace of mind between appointments.", price:"$45", tag:"Monitoring", tagType:"monitoring", digs:876, rating:4.6, reviews:"8.4k" },
    { brand:"Kindred Bravely", name:"Frankie Seamless Nursing Bra (TTC-safe)", desc:"Works through pregnancy and nursing. No underwire, supremely soft, adjustable as you grow.", price:"$38", tag:"Comfort", tagType:"comfort", digs:742, rating:4.7, reviews:"6.1k" },
    { brand:"Earth Mama", name:"Belly Butter", desc:"Safe for pregnancy. No retinoids, no harsh chemicals. Rich shea + cocoa butter. OB approved.", price:"$18", tag:"Skincare", tagType:"skincare", digs:634, rating:4.8, reviews:"9.8k" },
    { brand:"Lamaze", name:"Breathe, See, Feel Birth Class (Online)", desc:"Self-paced video birth prep course. Covers all pain management options, partner support, what to bring.", price:"$40", tag:"Education", tagType:"education", digs:521, rating:4.7, reviews:"3.2k" },
  ];
  const filtered = filter==="all"?PRODUCTS:PRODUCTS.filter(p=>p.tagType===filter);
  const tc = { vitamins:{bg:C.primaryL,color:C.primaryDk,border:C.primaryM}, comfort:{bg:C.ppL,color:C.ppD,border:C.ppM}, monitoring:{bg:C.blueL,color:C.blueD,border:"#85b7eb"}, skincare:{bg:C.pinkL,color:C.pinkDk,border:C.pinkM}, education:{bg:C.amL,color:C.am,border:"#f5c97a"} };

  return (
    <div style={{ fontFamily:"'Plus Jakarta Sans',-apple-system,sans-serif" }}>
      <div style={{ padding:"0 0 24px" }}>
        <NovaBanner text="You're at week 20 — Nova recommends the pregnancy pillow now (sleep gets harder fast), and the fetal doppler for between-appointment peace of mind." prompts={["Is this safe in pregnancy?","Best pillow for back pain","Which prenatal has DHA?"]} />
        <div style={{ display:"flex", gap:6, alignItems:"center", marginBottom:14, flexWrap:"wrap" }}>
          <span style={{ fontSize:10, fontWeight:700, letterSpacing:".08em", textTransform:"uppercase", color:C.muted, marginRight:4 }}>Filter</span>
          {[["all","All"],["vitamins","Vitamins"],["comfort","Comfort"],["monitoring","Monitoring"],["skincare","Skincare"],["education","Classes"]].map(([id,label])=>(
            <button key={id} onClick={()=>setFilter(id)} style={{ fontFamily:"inherit", fontSize:11, fontWeight:600, padding:"5px 14px", borderRadius:999, border:`1px solid ${filter===id?C.primaryM:C.border}`, background:filter===id?C.primaryL:C.sand, color:filter===id?C.primaryDk:C.muted, cursor:"pointer" }}>{label}</button>
          ))}
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:14 }}>
          {filtered.map(p=>{
            const colors = tc[p.tagType]||{bg:C.sand2,color:C.muted,border:C.border};
            return (
              <div key={p.name} style={{ background:C.white, border:`${p.featured?"2px":"1px"} solid ${p.featured?C.primary:C.border}`, borderRadius:C.r, overflow:"hidden", display:"flex", flexDirection:"column", position:"relative" }}>
                {p.featured && <div style={{ position:"absolute", top:-1, left:14, background:C.primary, color:"#fff", fontSize:10, fontWeight:700, padding:"3px 10px", borderRadius:"0 0 8px 8px" }}>Nova's top pick</div>}
                <div style={{ height:120, background:C.sand, display:"flex", alignItems:"center", justifyContent:"center", fontSize:32, position:"relative" }}>
                  🤰
                  <div style={{ position:"absolute", top:10, left:10, background:"rgba(255,253,249,.92)", borderRadius:999, padding:"3px 8px", fontSize:10, fontWeight:700, color:C.text }}>★ {p.rating} <span style={{ fontSize:10, color:C.muted }}>({p.reviews})</span></div>
                  <div style={{ position:"absolute", bottom:8, right:8, background:C.primary, color:"#fff", fontSize:11, fontWeight:800, padding:"5px 11px", borderRadius:999 }}>{p.digs} digs</div>
                </div>
                <div style={{ padding:"12px 14px", flex:1, display:"flex", flexDirection:"column" }}>
                  <div style={{ fontSize:10, fontWeight:700, letterSpacing:".1em", textTransform:"uppercase", color:C.muted, marginBottom:3 }}>{p.brand}</div>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:4 }}>{p.name}</div>
                  <div style={{ fontSize:11, color:C.muted, lineHeight:1.45, flex:1, marginBottom:10 }}>{p.desc}</div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                    <div style={{ fontSize:16, fontWeight:700, color:C.text }}>{p.price}</div>
                    <span style={{ fontSize:11, fontWeight:600, padding:"4px 10px", borderRadius:999, background:colors.bg, color:colors.color, border:`1px solid ${colors.border}` }}>{p.tag}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div style={{ background:C.primary, borderRadius:C.r, padding:"16px 20px", display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
          <div>
            <div style={{ fontSize:13, fontWeight:600, color:"#fff" }}>Sign up to access the full pregnancy catalog</div>
            <div style={{ fontSize:11, color:"rgba(255,255,255,0.8)", marginTop:2 }}>200+ pregnancy products — all screened for safety</div>
          </div>
          <button style={{ fontFamily:"inherit", fontSize:12, fontWeight:700, color:C.primaryDk, background:"#fff", border:"none", borderRadius:999, padding:"8px 18px", cursor:"pointer", whiteSpace:"nowrap" }}>Sign up free →</button>
        </div>
      </div>
    </div>
  );
}
